


// src/Screens/admin/Support/SupportTicketManagement.jsx
import { useState, useEffect, useContext } from "react";
import { Form, InputGroup, Table, Spinner } from "react-bootstrap";
import {
  FiFilter,
  FiMessageSquare,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../assets/css/admin/screens/supportticketmanagement/AdminSupportTicketMangement.css";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout";
import AppContext from "../../../contexts/AppContext";

const SupportTicketsManagement = () => {
  const navigate = useNavigate();
  const { baseUrl, setSelectedConversation, adminAuthToken} = useContext(AppContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterUserType, setFilterUserType] = useState("All");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const viewAllTickets = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/support/viewAllTickets`, {
        headers: { "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${adminAuthToken}`,
         },
      })
      .then((response) => { setTickets(response.data.tickets || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const viewTicketStats = () => {
    axios
      .get(`${baseUrl}/admin/support/stats`, {
        headers: { "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${adminAuthToken}`,
         },
      })
      .then((response) => setStats(response.data))
      .catch();
  };

  useEffect(() => {
    viewAllTickets();
    viewTicketStats();
  }, []);

  const getTicketUserType = (conversation) => {
    if (conversation.chseller_id || conversation.seller) return "Seller";
    if (conversation.chcustomer_id || conversation.customer) return "Customer";
    return "Unknown";
  };

  const getTicketName = (conversation) => {
    if (conversation.seller)
      return conversation.seller.store_name || conversation.seller.email || "—";
    if (conversation.customer)
      return `${conversation.customer.first_name ?? ""} ${conversation.customer.last_name ?? ""}`.trim() || "—";
    return "—";
  };

  const getTicketEmail = (conversation) => {
    if (conversation.seller) return conversation.seller.email || conversation.seller.business_email || "—";
    if (conversation.customer) return conversation.customer.email || "—";
    return "—";
  };

  const filteredTickets = tickets.filter((conversation) => {
    const ticket = conversation.supporthelp;
    const userType = getTicketUserType(conversation);
    const name = getTicketName(conversation);
    const email = getTicketEmail(conversation);
    const matchesSearch =
      ticket?.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(conversation.id).includes(searchTerm);
    const matchesStatus = filterStatus === "All" || ticket?.status === filterStatus;
    const matchesUserType = filterUserType === "All" || userType === filterUserType;
    return matchesSearch && matchesStatus && matchesUserType;
  });

  const getStatusClass = (status) => {
    switch (status) {
      case "Open":       return "stm-status--open";
      case "In Progress":return "stm-status--progress";
      case "Resolved":   return "stm-status--resolved";
      case "Rejected":   return "stm-status--rejected";
      default:           return "stm-status--open";
    }
  };

  const getUserTypeClass = (userType) => {
    if (userType === "Customer") return "stm-user--customer";
    if (userType === "Seller")   return "stm-user--seller";
    return "";
  };

  return (
    <AdminMasterLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="stm-card-shell">

        {/* Header */}
        <div className="stm-header-section">
          <h3 className="stm-section-title">Support Ticket Management</h3>
        </div>

        {/* Stat Cards */}
        <div className="stm-summary-grid">
          <div className="stm-summary-card">
            <div>
              <span className="stm-label">Total Tickets</span>
              <h3>{stats?.total_tickets ?? "0"}</h3>
              <p className="stm-muted mb-0">All support requests</p>
            </div>
            <div className="stm-card-icon"><FiMessageSquare size={22} /></div>
          </div>
          <div className="stm-summary-card">
            <div>
              <span className="stm-label">In Progress</span>
              <h3>{stats?.inprogress_tickets ?? "0"}</h3>
              <p className="stm-muted mb-0">Being handled</p>
            </div>
            <div className="stm-card-icon"><FiClock size={22} /></div>
          </div>
          <div className="stm-summary-card">
            <div>
              <span className="stm-label">Resolved</span>
              <h3>{stats?.resolved_tickets ?? "0"}</h3>
              <p className="stm-muted mb-0">Successfully closed</p>
            </div>
            <div className="stm-card-icon stm-card-icon--green"><FiCheckCircle size={22} /></div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="stm-toolbar">
          <InputGroup className="stm-search-group">
            <InputGroup.Text className="stm-search-icon"><FiSearch size={14} /></InputGroup.Text>
            <Form.Control
              className="stm-search-input"
              placeholder="Search tickets by ID, name, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <div className="stm-filter-group">
            <FiFilter size={14} />
            <Form.Select className="stm-filter-select" value={filterUserType} onChange={(e) => setFilterUserType(e.target.value)}>
              <option value="All">All Users</option>
              <option value="Customer">Customers</option>
              <option value="Seller">Sellers</option>
            </Form.Select>
            <Form.Select className="stm-filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="All">All Status</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
              <option value="Rejected">Rejected</option>
            </Form.Select>
          </div>
        </div>

        {/* Table */}
        <div className="stm-layout">
          <div className="stm-table-wrapper">
            <Table className="stm-table" responsive>
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>User</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <Spinner animation="border" size="sm" className="stm-spinner" />
                      <span className="ms-2 stm-muted stm-loading-text">Loading tickets...</span>
                    </td>
                  </tr>
                )}

                {!loading && filteredTickets.length > 0 && filteredTickets.map((conversation) => {
                  const ticket = conversation.supporthelp;
                  const userType = getTicketUserType(conversation);
                  const name = getTicketName(conversation);
                  const email = getTicketEmail(conversation);
                  return (
                    <tr key={conversation.id}>
                      <td><span className="stm-ticket-id">#{conversation.id}</span></td>
                      <td>
                        <div className="stm-customer">
                          <div className="stm-customer-avatar">
                            {userType === "Customer" ? <FiUser size={14} /> : <FiShoppingBag size={14} />}
                          </div>
                          <div className="stm-customer-info">
                            <p className="stm-customer-name">{name}</p>
                            <p className="stm-customer-email">{email}</p>
                          </div>
                        </div>
                      </td>
                      <td><span className={`stm-user-type ${getUserTypeClass(userType)}`}>{userType}</span></td>
                      <td><span className="stm-category-badge">{ticket?.category || "—"}</span></td>
                      <td><p className="stm-subject-text">{ticket?.description || "—"}</p></td>
                      <td><span className={`stm-status ${getStatusClass(ticket?.status)}`}>{ticket?.status || "—"}</span></td>
                      <td>
                        <div className="stm-actions">
                          <button className="stm-link" onClick={() => { setSelectedConversation(conversation); navigate("/admin/support/ticket-detail"); }}>
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!loading && filteredTickets.length === 0 && (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <div className="stm-empty-icon">📨</div>
                      <p className="stm-muted mt-2">
                        {tickets.length === 0 ? "No tickets found" : "No tickets match the selected filters"}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

      </div>
    </AdminMasterLayout>
  );
};

export default SupportTicketsManagement;
